import {Choice, Switch, useTranslationContext} from 'platform/components';

import {suffixTestId} from 'shared/web';

import {PageSizeSettingsOptions} from '../constants/PageSizeSettingsOptions';
import {SETTINGS_UPDATED_EVENT} from '../constants/SETTINGS_UPDATED_EVENT';
import {useDataGridContext} from '../context/useDataGridContext';
import {FeGridSettings} from '../types/Api';
import {SettingsToolpanelRendererProps} from '../types/SettingsToolpanelRenderer';
import {SettingsUpdatedPayload} from '../types/SettingsUpdatedPayload';
import {LineHeight} from '../utils/lineHeightToValue';

const DEFAULT_PAGINATION_SIZE = 25;

export type RowHeightOption = {
  value: LineHeight;
  label: string;
  cyId: string;
};

export function SettingsToolpanelRenderer({
  settings,
  gridApi,
  setSettings,
  ...props
}: SettingsToolpanelRendererProps) {
  const t = useTranslationContext();
  const {gridCode} = useDataGridContext();

  const updateSettings = (key: keyof FeGridSettings, value: any) => {
    window.dispatchEvent(
      new CustomEvent<SettingsUpdatedPayload>(SETTINGS_UPDATED_EVENT, {
        detail: {
          gridCode,
        },
      })
    );
    const newSettings = {...settings, [key]: value};
    gridApi?.dispatchEvent({type: 'stateUpdated'});
    setSettings(newSettings);
  };

  const ValueWrappingOptions = [
    {value: 'TRUNCATE', label: t('page.datagrid.labels.valueWrappingTruncate')},
    {value: 'WRAP', label: t('page.datagrid.labels.valueWrappingWrap')},
  ];

  const rowHeightOptions: RowHeightOption[] = [
    {
      value: 'COMPACT',
      label: t('page.datagrid.labels.rowHeightCompact'),
      cyId: 'selectItem-settings.rowHeightCompact',
    },
    {
      value: 'SHORT',
      label: t('page.datagrid.labels.rowHeightSmall'),
      cyId: 'selectItem-settings.rowHeightSmall',
    },
    {
      value: 'DEFAULT',
      label: t('page.datagrid.labels.rowHeightDefault'),
      cyId: 'selectItem-settings.rowHeightDefault',
    },
    {
      value: 'TALL',
      label: t('page.datagrid.labels.rowHeightTall'),
      cyId: 'selectItem-settings.rowHeighttall',
    },
    {
      value: 'THUMBNAIL',
      label: t('page.datagrid.labels.rowHeightLargeThumbnail'),
      cyId: 'selectItem-settings.largeThumbnail',
    },
  ];

  const onHeaderHeightChange = (value: string | number | string[] | null) =>
    updateSettings('headerHeight', value);

  const onRowHeightChange = (value: string | number | string[] | null) =>
    updateSettings('rowHeight', value);

  const onPageSizeChange = (value: string | number | string[] | null) =>
    updateSettings('itemsPerPage', value ?? DEFAULT_PAGINATION_SIZE);

  const onRowZebraChange = (value: boolean) => updateSettings('stripedRows', value);

  const onVerticalSeparatorChange = (value: boolean) =>
    updateSettings('verticalSeparatorsVisible', value);

  const onHorizontalSeparatorChange = (value: boolean) =>
    updateSettings('horizontalSeparatorsVisible', value);

  const onValieWrappingChange = (value: string | number | string[] | null) =>
    updateSettings('valueWrapMode', value);

  const onKeyboardNavigationChange = (value: boolean) =>
    updateSettings('keyboardNavigationEnabled', value);

  const onColumnGroupsInHeaderChange = (value: boolean) =>
    updateSettings('columnGroupsVisible', value);

  return (
    <>
      {/* eslint-disable-next-line react/forbid-dom-props */}
      <div className="ag-panel-content">
        <Choice
          label={t('page.datagrid.labels.headerHeight')}
          options={rowHeightOptions}
          value={settings.headerHeight}
          onChange={onHeaderHeightChange}
          name="headerHeight"
          data-testid={suffixTestId('headerHeight', props)}
          isNotClearable
        />
      </div>
      {/* eslint-disable-next-line react/forbid-dom-props */}
      <div className="ag-panel-content">
        <Choice
          label={t('page.datagrid.labels.rowHeight')}
          options={rowHeightOptions}
          value={settings.rowHeight}
          onChange={onRowHeightChange}
          name="rowHeight"
          data-testid={suffixTestId('rowHeight', props)}
          isNotClearable
        />
      </div>
      {/* eslint-disable-next-line react/forbid-dom-props */}
      <div className="ag-panel-content">
        <Choice
          label={t('page.datagrid.labels.pageSize')}
          onChange={onPageSizeChange}
          options={PageSizeSettingsOptions}
          value={settings.itemsPerPage === null ? DEFAULT_PAGINATION_SIZE : settings.itemsPerPage}
          name="itemsPerPage"
          data-testid={suffixTestId('pageSize', props)}
          isNotClearable
        />
      </div>
      {/* eslint-disable-next-line react/forbid-dom-props */}
      <div className="ag-panel-content">
        <Choice
          label={t('page.datagrid.labels.valueWrapping')}
          onChange={onValieWrappingChange}
          options={ValueWrappingOptions}
          value={settings.valueWrapMode}
          name="itemsPerPage"
          data-testid={suffixTestId('valueWrapping', props)}
          isNotClearable
        />
      </div>
      {/* eslint-disable-next-line react/forbid-dom-props */}
      <div className="ag-panel-content">
        <Switch
          label={t('page.datagrid.labels.rowZebra')}
          value={settings.stripedRows}
          onChange={onRowZebraChange}
          data-testid={suffixTestId('rowZebra', props)}
        />
      </div>
      {/* eslint-disable-next-line react/forbid-dom-props */}
      <div className="ag-panel-content">
        <Switch
          label={t('page.datagrid.labels.rowBorder')}
          value={settings.horizontalSeparatorsVisible}
          onChange={onHorizontalSeparatorChange}
          data-testid={suffixTestId('rowBorder', props)}
        />
      </div>
      {/* eslint-disable-next-line react/forbid-dom-props */}
      <div className="ag-panel-content">
        <Switch
          label={t('page.datagrid.labels.colBorder')}
          value={settings.verticalSeparatorsVisible}
          onChange={onVerticalSeparatorChange}
          data-testid={suffixTestId('colBorder', props)}
        />
      </div>
      {/* eslint-disable-next-line react/forbid-dom-props */}
      <div className="ag-panel-content">
        <Switch
          label={t('page.datagrid.labels.keyboardNav')}
          value={settings.keyboardNavigationEnabled}
          onChange={onKeyboardNavigationChange}
          data-testid={suffixTestId('keyboardNav', props)}
        />
      </div>
      {/* eslint-disable-next-line react/forbid-dom-props */}
      <div className="ag-panel-content">
        <Switch
          label={t('page.datagrid.labels.showColGroups')}
          value={settings.columnGroupsVisible}
          onChange={onColumnGroupsInHeaderChange}
          data-testid={suffixTestId('keyboardNav', props)}
        />
      </div>
    </>
  );
}
